import { useEffect, useState } from "react";
import { Loader2, HeartPulse, RefreshCw } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

const POLICIES = [
    { id: "fallback", label: "Smart fallback", hint: "Skip an unhealthy MID only if the partner has another healthy MID." },
    { id: "flag_only", label: "Flag only", hint: "Show the warning but keep routing to the MID as usual." },
    { id: "always_skip", label: "Always skip", hint: "Never route to an unhealthy MID. Orders fail fast if none are healthy." },
];

export const timeAgo = (iso) => {
    if (!iso) return "never";
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
};

export function PayinHealthBar({ onRan }) {
    const [cfg, setCfg] = useState(null);
    const [running, setRunning] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        try { const r = await api.get("/admin/payin/health"); setCfg(r.data); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        setRunning(true); setErr("");
        try { await api.post("/admin/payin/health/run"); await load(); onRan?.(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setRunning(false); }
    };
    const setPolicy = async (id) => {
        if (id === cfg?.blocked_mid_policy) return;
        setSaving(true); setErr("");
        try { const r = await api.patch("/admin/payin/health", { blocked_mid_policy: id }); setCfg(r.data); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setSaving(false); }
    };
    const active = POLICIES.find((p) => p.id === cfg?.blocked_mid_policy) || POLICIES[0];

    return (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-3 space-y-3" data-testid="payin-health-bar">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><HeartPulse className="w-4 h-4" /></span>
                    <div>
                        <div className="text-sm font-semibold">Auto health check</div>
                        <div className="text-[11px] text-slate-500" data-testid="payin-health-last-run">
                            Runs every {cfg?.interval_minutes || 60} min · last run {timeAgo(cfg?.last_run_at)}{cfg?.last_run_source ? ` (${cfg.last_run_source})` : ""}
                        </div>
                    </div>
                </div>
                <button onClick={runNow} disabled={running} data-testid="payin-health-run-now"
                        className="px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50 self-start sm:self-auto">
                    {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Check all now
                </button>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">When a MID is unhealthy</div>
                <div className="inline-flex flex-wrap bg-slate-100 rounded-lg p-0.5 gap-0.5" data-testid="payin-health-policy">
                    {POLICIES.map((p) => (
                        <button key={p.id} onClick={() => setPolicy(p.id)} disabled={saving} data-testid={`payin-policy-${p.id}`}
                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${active.id === p.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                            {p.label}
                        </button>
                    ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5" data-testid="payin-health-policy-hint">{active.hint}</p>
            </div>
            {err && <div className="text-xs text-rose-700" data-testid="payin-health-error">{err}</div>}
        </div>
    );
}
